import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UnknownAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { setOrganization, setOrganizations } from "../../model/app/actions";
import { login, setMe, setUser } from "../../model/oidc/actions";
import { Me, User } from "../../types/api-schema";
import { BROADCAST_MSG_LOGIN, LS_ORGANIZATION_KEY } from "../constants";
import fetchUserInfo from "../fetchUserInfo";
import { getAdminOrgOrConfig } from "../geopsAdminUtils";
import handleSessionExpiry from "../handleSessionExpiry";

import useAppSelector from "./useAppSelector";

export default function useAuthenticate() {
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const ssoConfigsUrl = useAppSelector((state) => {
    return state.app.ssoConfigsUrl;
  });
  const userInfoUrl = useAppSelector((state) => {
    return state.app.userInfoUrl;
  });
  const meUrl = useAppSelector((state) => {
    return state.app.meUrl;
  });
  const apiUrl = useAppSelector((state) => {
    return state.app.apiUrl;
  });
  const broadcast = useAppSelector((state) => {
    return state.oidc.broadcast;
  });
  const [userInfo, setUserInfo] = useState<null | User>(null);
  const [me, setMeInfo] = useState<Me | null>(null);
  const [isAuthComplete, setIsAuthComplete] = useState(false);
  const [isUserInfoComplete, setIsUserInfoComplete] = useState(false);

  useEffect(() => {
    const abortCtrlLogin = new AbortController();
    const getUserInfo = async () => {
      const newUserInfo = await fetchUserInfo(userInfoUrl, {
        signal: abortCtrlLogin.signal,
      });
      // Access state, role, plan and entitlements, only for logged-in users
      const newMe =
        newUserInfo && meUrl
          ? await fetchUserInfo<Me>(meUrl, { signal: abortCtrlLogin.signal })
          : null;
      if (abortCtrlLogin.signal.aborted) {
        return;
      }
      setMeInfo(newMe);
      setIsUserInfoComplete(true);
      setUserInfo(newUserInfo);
    };
    getUserInfo();

    return () => {
      abortCtrlLogin.abort();
    };
  }, [ssoConfigsUrl, userInfoUrl, meUrl]);

  useEffect(() => {
    // Keycloak: send the user to login once the session ended
    if (me?.auth_provider !== "keycloak" || !apiUrl) {
      return undefined;
    }
    handleSessionExpiry(apiUrl, () => {
      dispatch(login());
    });
    return () => {
      handleSessionExpiry(apiUrl, null);
    };
  }, [apiUrl, dispatch, me]);

  useEffect(() => {
    if (userInfo && broadcast) {
      broadcast.postMessage(BROADCAST_MSG_LOGIN);
    }
  }, [broadcast, userInfo]);

  useEffect(() => {
    const updateConfig = async () => {
      const userOrganizations = userInfo?.organizations || [];
      dispatch(setOrganizations(userOrganizations));

      const savedOrganizationSlug = localStorage.getItem(LS_ORGANIZATION_KEY);
      const adminOrgOrConfig = getAdminOrgOrConfig(
        null,
        userOrganizations,
        userInfo,
      );
      const currentOrganization =
        // Prioritize the organization saved in localStorage if the user still has access to it.
        userOrganizations.find((org) => {
          return org.slug === savedOrganizationSlug;
        }) ||
        // Fall back to the admin organization or the first available organization.
        userOrganizations.find((org) => {
          return org.slug === adminOrgOrConfig?.slug;
        }) ||
        userOrganizations[0];
      dispatch(setOrganization(currentOrganization));
      setIsAuthComplete(true);
    };

    if (isUserInfoComplete) {
      dispatch(setUser(userInfo));
      dispatch(setMe(me));
      updateConfig();
    }
  }, [dispatch, isUserInfoComplete, me, userInfo]);

  return { isAuthComplete, me, userInfo };
}
